import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import AuthAxios from '../../../axios';
import ReportImage from '../../../assets/images/file_report.png';
import formatDurationDisplay from '../../../utils/formatDurationDisplay';

type LocaleString = {
  en: string;
  ko: string;
};

type Audit = {
  createdAt: string;
  createdBy: string | null;
  deletedAt: string | null;
  deletedBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
};

type Track = {
  id: string;
  tag: LocaleString;
  url: string;
  audit: Audit;
  title: LocaleString;
  isFree: boolean;
  status: string;
  artwork: string;
  duration: number;
  legacyId: number | null;
  likeCount: number;
  streamUrl: string | null;
  thumbnail: string;
  trialTrack: string;
  description: LocaleString;
  inactivatedAt: string | null;
  matchingStrength: string | null;
};

type SurveyCategoryResult = {
  title: LocaleString;
  track: Track;
  descriptions: LocaleString[];
  sleepMatchingStrength?: string;
  stressMatchingStrength?: string;
};

type StateBasedTrackList = {
  tracks: Track[];
  stateTargetKeyword: string;
  trackCategory: {ko: string; en: string; };
};

type SurveyResult = {
  sleep: SurveyCategoryResult;
  stress: SurveyCategoryResult;
  stateBasedTrackList: StateBasedTrackList;
  preferencesTrackList: Track[];
  matchingStrengthAverage: string;
};

type SubmissionAudit = {
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  deletedBy: string | null;
};

type TestResultResponse = {
  id: string;
  results: {
    user: {
      id: string;
      nickname: string;
    };
    surveyResult: SurveyResult;
  };
  submissionAudit: SubmissionAudit;
};

const maxWidth = 1200;
const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: maxWidth,
  borderRadius: 10,
  background: 'var(--gray-900, #191F28)',
  boxShadow: '0 4px 34px 0 rgba(0, 0, 0, 0.25)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
};

const intensityFillMap: Record<string, number> = {
  A: 6,
  B: 5,
  C: 3,
  D: 2,
  E: 1,
  F: 0,
};

const IntensityDots = ({ level }: { level?: string }) => {
  const fillCount = intensityFillMap[level?.toUpperCase?.() ?? ''] ?? 0;

  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 25, width: '100%' }}>
      {Array.from({ length: 6 }).map((_, idx) => {
        const isFilled = idx < fillCount;
        return (
          <div
            key={idx}
            style={{
              flex: 1,
              height: 12,
              borderRadius: 12,
              backgroundColor: isFilled ? '#4F68FC' : 'transparent',
              border: `1px solid ${isFilled ? '#4F68FC' : '#4E5968'}`,
              boxShadow: isFilled ? '0 0 8px #4F68FC' : 'none',
              minWidth: 0,
            }}
          />
        );
      })}
    </div>
  );
};
export default function TestResult() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const [testResult, setTestResult] = React.useState<TestResultResponse | null>(null);
  const locale = React.useMemo<'ko' | 'en'>(() => {
    const primary = navigator.languages?.[0] ?? navigator.language ?? 'ko';
    return primary.toLowerCase().startsWith('en') ? 'en' : 'ko';
  }, []);

  const pickLocaleString = (text?: LocaleString) => {
    if (!text) return '';
    return locale === 'en' ? text.en || text.ko || '' : text.ko || text.en || '';
  };

  const t = (ko: string, en: string) => (locale === 'en' ? en : ko);

  const getTestResult = (paramId: string) => {
    AuthAxios.get<TestResultResponse>(`/questionnaire/submission/shared?id=${paramId}`)
      .then((response) => {
        console.log('Test result data:', response.data);
        setTestResult(response.data);
      })
      .catch((error) => {
        console.error('Error fetching test result:', error);
      });
  };

  useEffect(() => {
    console.log('TestResult mounted with id:', id);
    if (id) {
      getTestResult(id);
    }
  }, [id]);

  return (
    <div
      style={{
        backgroundColor: 'rgb(19 24 30)',
        color: '#FFFFFF',
        height: '100vh',
        overflow: 'scroll',
        padding: '0 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          height: '100%',
          width: '100%',
          maxWidth: maxWidth,
        }}
      >
        <div
          style={{
            textAlign: 'center',
            fontSize: 24,
            fontWeight: 600,
            lineHeight: '34px',
            letterSpacing: '-0.6px',
            paddingTop: 20,
          }}
        >
          <div>
            {locale === 'en'
              ? `${testResult?.results.user.nickname}'s`
              : `${testResult?.results.user.nickname} 님의`}
          </div>
          <div>{t('현재 상태 분석 결과예요', 'Here is your current status analysis')}</div>
        </div>
        <div
          style={{
            ...cardStyle,
            border: '1px solid var(--gray-700, #4E5968)',
            marginTop: 20,
          }}
        >
          <div style={{ color: '#8B95A1', fontSize: '16px' }}>{t('스트레스,수면 등급', 'Stress / Sleep grade')}</div>
          <div
            style={{
              color: 'var(--gray-white, #FFF)',
              textAlign: 'center',
              fontSize: 40,
              fontWeight: 900,
              lineHeight: '150%',
            }}
          >
            LEVEL. <span style={{ color: '#28C8A8' }}>{testResult?.results.surveyResult.matchingStrengthAverage}</span>
          </div>
        </div>
        <div
          style={{
            ...cardStyle,
            marginTop: 40,
            alignItems: 'initial',
          }}
        >
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {t('스트레스 상태', 'Stress status')}
            <span style={{ color: '#4F68FC', paddingLeft: 10 }}>
              {testResult?.results.surveyResult.stress.stressMatchingStrength}
            </span>
            <div style={{ color: '#D1D6DB', fontSize: 16, fontWeight: 400, marginTop: 10 }}>
              {testResult?.results.surveyResult.stress.descriptions?.map((desc, idx) => (
                <div key={desc.ko ?? idx}>{locale === 'en' ? desc.en ?? desc.ko : desc.ko ?? desc.en}</div>
              ))}
            </div>
            <IntensityDots level={testResult?.results.surveyResult.stress.stressMatchingStrength} />
          </div>
        </div>
        <div style={{ width: '100%', padding: '20px 0', fontWeight: 700 }}>
          {t('맞춤 스트레스 위드닥터 사운드필 이예요.', 'Your tailored stress Soundpill.')}
        </div>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img
            src={`${process.env.REACT_APP_IMG_ENDPOINT}/${testResult?.results.surveyResult.stress.track.thumbnail}`}
            alt="Soundpill Recommendation Stress Track Image"
            style={{ width: '100%', objectFit: 'cover', height: 350 }}
          />
          <div style={{ fontSize: 24, fontWeight: 600, padding: '15px 0', textAlign: 'center' }}>
            {pickLocaleString(testResult?.results.surveyResult.stress.track.title)}
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <div>{formatDurationDisplay(testResult?.results.surveyResult.stress.track.duration ?? 0)}</div>
            <div>#{(pickLocaleString(testResult?.results.surveyResult.stress.track.tag) ?? '').replace(/,/g, '#')}</div>
          </div>
          <a
            href="https://limh6.app.link/2fvaWtqM1Yb"
            style={{
              borderRadius: 50,
              background: '#4F68FC',
              display: 'flex',
              width: 140,
              height: 60,
              padding: 16,
              justifyContent: 'center',
              alignItems: 'center',
              gap: 10,
              marginTop: 16,
            }}
          >
            {t('재생', 'Play')}
          </a>
        </div>
        <div
          style={{
            ...cardStyle,
            alignItems: 'initial',
            marginTop: 40,
          }}
        >
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {t('수면 상태', 'Sleep status')}
            <span style={{ color: '#4F68FC', paddingLeft: 10 }}>
              {testResult?.results.surveyResult.sleep.sleepMatchingStrength}
            </span>
            <div style={{ color: '#D1D6DB', fontSize: 16, fontWeight: 400, marginTop: 10 }}>
              {testResult?.results.surveyResult.sleep.descriptions?.map((desc, idx) => (
                <div key={desc.ko ?? idx}>{locale === 'en' ? desc.en ?? desc.ko : desc.ko ?? desc.en}</div>
              ))}
            </div>
            <IntensityDots level={testResult?.results.surveyResult.sleep.sleepMatchingStrength} />
          </div>
        </div>
        <div style={{ width: '100%', padding: '20px 0', fontWeight: 700 }}>
          {t('맞춤 수면 위드닥터 사운드필 이예요.', 'Your tailored sleep Soundpill.')}
        </div>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img
            src={`${process.env.REACT_APP_IMG_ENDPOINT}/${testResult?.results.surveyResult.sleep.track.thumbnail}`}
            alt="Soundpill Recommendation Sleep Track Image"
            style={{ width: '100%', objectFit: 'cover', height: 350 }}
          />
          <div style={{ fontSize: 24, fontWeight: 600, padding: '15px 0', textAlign: 'center' }}>
            {pickLocaleString(testResult?.results.surveyResult.sleep.track.title)}
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <div>{formatDurationDisplay(testResult?.results.surveyResult.sleep.track.duration ?? 0)}</div>
            <div>#{(pickLocaleString(testResult?.results.surveyResult.sleep.track.tag) ?? '').replace(/,/g, '#')}</div>
          </div>
          <a
            href="https://limh6.app.link/2fvaWtqM1Yb"
            style={{
              borderRadius: 50,
              background: '#4F68FC',
              display: 'flex',
              width: 140,
              height: 60,
              padding: 16,
              justifyContent: 'center',
              alignItems: 'center',
              gap: 10,
              marginTop: 16,
            }}
          >
            {t('재생', 'Play')}
          </a>
        </div>
        <div style={{ textAlign: 'center', marginTop: 80, fontSize: 24, fontWeight: 600 }}>
          {locale === 'en' ? `${testResult?.results.user.nickname} needs` : `${testResult?.results.user.nickname}님이`}
          <br />
          {t('지금 필요한 사운드필', 'Soundpill you need now')}
        </div>
        <div style={{ backgroundColor: '#333D4B', borderRadius: '0 0 30px 30px' }}>
          <div
            style={{
              display: 'flex',
              gap: 20,
              marginTop: 30,
              alignItems: 'center',
              backgroundColor: '#191F28',
              justifyContent: 'center',
              padding: '20px',
            }}
          >
            <img
              src={`${process.env.REACT_APP_IMG_ENDPOINT}/${testResult?.results.surveyResult.stateBasedTrackList.tracks[0].thumbnail}`}
              alt="Soundpill Recommendation"
              style={{ objectFit: 'cover', width: '100%', maxWidth: 200, height: 200, borderRadius: 10 }}
            />
            <div style={{ width: '100%', maxWidth: 200, height: 200 }}>
              <div
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {pickLocaleString(testResult?.results.surveyResult.stateBasedTrackList.tracks[0].description)}
              </div>
              <div
                style={{
                  marginTop: 10,
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 6,
                  color: '#B0B8C1',
                  flexWrap: 'wrap',
                }}
              >
                <div> {pickLocaleString(testResult?.results.surveyResult.stateBasedTrackList.tracks[0].title)}</div>
                <div>
                  {formatDurationDisplay(testResult?.results.surveyResult.stateBasedTrackList.tracks[0].duration ?? 0)}
                </div>
              </div>
            </div>
          </div>
          <a
            href="https://limh6.app.link/2fvaWtqM1Yb"
            style={{ height: 50, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            {locale === 'en'
              ? `See more ${pickLocaleString(testResult?.results.surveyResult.stateBasedTrackList.trackCategory)} Soundpill`
              : `${testResult?.results.surveyResult.stateBasedTrackList.stateTargetKeyword} 사운드필 더 보기`}
          </a>
        </div>
        <div style={{ textAlign: 'center', marginTop: 40, fontSize: 24, fontWeight: 600 }}>
          {locale === 'en' ? `${testResult?.results.user.nickname}'s` : `${testResult?.results.user.nickname}님의`}
          <br />
          {t('취향을 저격하는 사운드필', 'Soundpills tailored to your taste')}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap'}}>
        {testResult?.results.surveyResult.preferencesTrackList.slice(0, 4).map((track) => (
          <a href="https://limh6.app.link/2fvaWtqM1Yb" key={track.id} style={{ width: '50%', display: 'flex', flexDirection: 'column', padding: 10 }}>
            <img
              src={`${process.env.REACT_APP_IMG_ENDPOINT}/${track.thumbnail}`}
              alt={pickLocaleString(track.title)}
              style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 10 }}
            />
            <div
              style={{
                width: '100%',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                marginTop: 8,
              }}
            >
              {pickLocaleString(track.title)}
              <div style={{fontSize: 12, color: '#8B95A1'}}>{formatDurationDisplay(track.duration ?? 0)}</div>
            </div>
          </a>
        ))}
        </div>
        <div style={{display:'flex', justifyContent: 'center', alignItems: 'center'}}>
          <img src={ReportImage} alt="Report" style={{ width: 185, marginTop: 20, borderRadius: 10 }} />
        </div>
        <div style={{textAlign: 'center', fontWeight: 700, fontSize: 24}}>
          {t('나라면 어떤 결과가 나올까?', 'What result would you get?')}<br/>
          {t('지금 바로 테스트 해보세요', 'Try the test now')}
        </div>
        <div style={{display:'flex', justifyContent: 'center', alignItems: 'center', paddingBottom: 100}}>
          <a href="https://limh6.app.link/2fvaWtqM1Yb" style={{ width: '80%', textAlign: 'center', padding: 10, backgroundColor: '#4F68FC', maxWidth: 500, borderRadius: 50, fontSize: 16, fontWeight: 600, marginTop: 10, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 50 }}>
          {t('사운드필 테스트하기', 'Take the Soundpill test')}
          </a>
        </div>
      </div>
    </div>
  );
}
